import { Input } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { contacts as contactsService, type Contact } from "../../../api/contacts.service.js";
import styles from "./LawyerPicker.module.css";

/** Same rhythm and cap as the Link Lawyer modal and CompanyPicker: the search
 * beside this field debounces at 300ms, and a field that felt different for no
 * reason would be noticed. */
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 20;

/**
 * The two things the form needs from a contact: the Address Book uuid the write
 * body carries, and the name the field shows. Deliberately not the full
 * `Contact` — the edit form has to seed this from an award row, which holds
 * exactly these two and nothing else about the person.
 */
export type LawyerPickerValue = {
  id: string;
  name: string;
};

type LawyerPickerProps = {
  value: LawyerPickerValue | null;
  onChange: (value: LawyerPickerValue | null) => void;
  /**
   * The Address Book company whose contacts are searched. Null disables the
   * field: as in the Link Lawyer modal, a lawyer is only ever picked from one
   * firm's roster, because `GET /v1/contacts` returns no company name and a
   * global result list would offer six people called "M. Cole" with nothing to
   * tell them apart.
   */
  companyId: string | null;
  label?: string;
  error?: string;
  disabled?: boolean;
  listboxId?: string;
};

/**
 * Searchable Address Book contact picker scoped to one company. Shape and
 * mechanics from `components/CompanyPicker`: debounced search, stale-response
 * guard, clearing the text clears the value.
 *
 * Not `components/ContactPicker`, which searches a mock rather than the API.
 */
export function LawyerPicker({
  value,
  onChange,
  companyId,
  label = "Associated Lawyer",
  error,
  disabled,
  listboxId = "lawyer-picker-listbox",
}: LawyerPickerProps) {
  const [searchText, setSearchText] = useState(value?.name ?? "");
  const [results, setResults] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setSearchText(value?.name ?? "");
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!companyId || !isOpen) return;
    if (value && searchText === value.name) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    setLoading(true);
    setSearchError(null);
    debounceRef.current = setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      try {
        // An empty box is a real search here: `companyId` alone returns the
        // firm's roster, which is browsable and is what the user wants when they
        // do not know how the Address Book spells the name.
        const response = await contactsService.getAll({
          companyId,
          search: searchText.trim() || undefined,
          limit: SEARCH_LIMIT,
        });
        if (requestId !== requestIdRef.current) return;
        setResults(response.contacts);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setResults([]);
        setSearchError("Couldn't load contacts. Try again.");
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
  }, [searchText, value, companyId, isOpen]);

  function handleInputChange(next: string) {
    setSearchText(next);
    setIsOpen(true);
    if (value && next !== value.name) {
      onChange(null);
    }
  }

  function handleSelect(contact: Contact) {
    // `contact.id`, the Address Book uuid — not `contact.contactId`, the legacy
    // WordPress term id the server derives from it.
    onChange({ id: contact.id, name: contact.name });
    setSearchText(contact.name);
    setResults([]);
    setIsOpen(false);
  }

  const isDisabled = disabled || companyId === null;
  const showDropdown = isOpen && companyId !== null;

  return (
    <div className={styles.picker} ref={containerRef}>
      <Input
        id="lawyer-picker-input"
        label={label}
        value={searchText}
        onChange={event => handleInputChange(event.target.value)}
        onFocus={() => setIsOpen(true)}
        error={error}
        helperText={companyId === null && !error ? "Select a firm first" : undefined}
        disabled={isDisabled}
        fullWidth
        autoComplete="off"
        placeholder="Search the firm's contacts by name…"
        role="combobox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
        aria-controls={listboxId}
      />
      {showDropdown && (
        <ul id={listboxId} role="listbox" className={styles.dropdown}>
          {loading && (
            <li className={styles.dropdownStatus} aria-live="polite">
              Searching…
            </li>
          )}
          {!loading && searchError && (
            <li className={styles.dropdownError} role="alert" aria-live="assertive">
              {searchError}
            </li>
          )}
          {!loading && !searchError && results.length === 0 && (
            <li className={styles.dropdownStatus} aria-live="polite">
              No contacts found at this firm
            </li>
          )}
          {!loading &&
            !searchError &&
            results.map(contact => {
              // Job title and office are what tell two people of one name apart
              // within a firm; one row per office is a normal Address Book shape.
              const detail = [contact.jobTitle, contact.officeName].filter(Boolean).join(" · ");
              return (
                <li
                  key={contact.id}
                  role="option"
                  aria-selected={value?.id === contact.id}
                  tabIndex={0}
                  className={styles.dropdownItem}
                  onClick={() => handleSelect(contact)}
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSelect(contact);
                    }
                  }}
                >
                  {contact.name}
                  {detail && <span className={styles.dropdownDetail}> · {detail}</span>}
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
}
